import type { Driver } from 'driver.js'
import { nextTick, ref } from 'vue'
import type { Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { navigateTo, useNuxtApp, useRoute } from '#imports'
import { createDriverConfig } from '~/onboarding/driverConfig'
import { resolveTourSteps } from '~/onboarding/dom'
import { matchesExpectedPath } from '~/onboarding/path'
import { shouldSkipTourByStoredState } from '~/onboarding/storage'
import type {
  OnboardingStateMap,
  OnboardingStep,
  OnboardingTourDefinition,
  OnboardingTourProgress,
  OnboardingTourProgressStatus,
} from '~/onboarding/types'
import { useOnboardingClickLock } from '~/composables/onboarding/useOnboardingClickLock'
import { useOnboardingStore } from '~~/stores/onboarding'

type DriverRuntimeDeps = {
  toolboxOpen: Ref<boolean>
  isToolboxUiBlocked: Ref<boolean>
  toastInfo: (title: string, message: string) => void
  resolveOnboardingStateMapForChecks: () => OnboardingStateMap
  updateTourProgress: (
    tour: OnboardingTourDefinition,
    status: OnboardingTourProgressStatus,
    currentStep: number
  ) => OnboardingStateMap
  persistOnboardingStateMap: (state: OnboardingStateMap) => void
  onStepChange: (tour: OnboardingTourDefinition, stepIndex: number) => void
  onClosed: (tour: OnboardingTourDefinition, isCompleted: boolean, stepIndex: number) => void
}

/** État + dépendances partagés par les fonctions du runtime Driver.js (voir fonctions ci-dessous). */
type DriverRuntimeState = {
  route: ReturnType<typeof useRoute>
  nuxtApp: ReturnType<typeof useNuxtApp>
  setStateMap: (state: OnboardingStateMap) => void
  activeDriver: Ref<Driver | null>
  activeTourId: Ref<string | null>
  activeTourSteps: Ref<OnboardingStep[] | null>
  pendingTeleportIndex: Ref<number | null>
  toolboxAnimationSyncTimer: Ref<number | null>
  isLaunchingTour: Ref<boolean>
  deactivateClickLock: () => void
  deps: DriverRuntimeDeps
}

function clearToolboxAnimationSyncTimer(state: DriverRuntimeState): void {
  if (state.toolboxAnimationSyncTimer.value === null) {
    return
  }
  window.clearTimeout(state.toolboxAnimationSyncTimer.value)
  state.toolboxAnimationSyncTimer.value = null
}

function stopCurrentTour(state: DriverRuntimeState): void {
  if (state.activeDriver.value === null) {
    return
  }
  if (state.activeDriver.value.isActive()) {
    state.activeDriver.value.destroy()
  }
  state.activeDriver.value = null
  state.activeTourId.value = null
  state.activeTourSteps.value = null
  state.pendingTeleportIndex.value = null
  state.deactivateClickLock()
  clearToolboxAnimationSyncTimer(state)
}

function isNextButtonDisabledForStep(state: DriverRuntimeState, stepIndex: number): boolean {
  const sourceStep = (state.activeTourSteps.value ?? [])[stepIndex]
  const isToolboxStepNotOpen = sourceStep?.openToolbox === true && !state.deps.toolboxOpen.value
  const isPathStepNotReached =
    !!sourceStep?.requiredPathPrefix &&
    sourceStep.requiredPathPrefix.length > 0 &&
    !state.route.path.startsWith(sourceStep.requiredPathPrefix)
  return isToolboxStepNotOpen || isPathStepNotReached
}

function syncNextButtonDisabledState(state: DriverRuntimeState, stepIndex: number): void {
  if (!state.activeDriver.value || !state.activeDriver.value.isActive()) {
    return
  }
  if (!import.meta.client) {
    return
  }
  const shouldDisable = isNextButtonDisabledForStep(state, stepIndex)
  const popover = document.querySelector('.driver-popover')
  const nextBtn = popover?.querySelector('button.driver-popover-next-btn, button.driver-popover-done-btn') as
    | HTMLButtonElement
    | null
  if (!nextBtn) {
    return
  }
  nextBtn.disabled = shouldDisable
  nextBtn.classList.toggle('driver-popover-btn-disabled', shouldDisable)
}

function handleRequestStepTeleport(
  state: DriverRuntimeState,
  targetIndex: number,
  candidate: OnboardingTourDefinition
): void {
  state.pendingTeleportIndex.value = targetIndex
  const step = candidate.steps[targetIndex]
  const expectedPath = step?.expectedPath
  if (expectedPath && !matchesExpectedPath(state.route.path, expectedPath)) {
    void navigateTo({ path: expectedPath })
    return
  }
  // Même page : on peut déplacer immédiatement.
  state.activeDriver.value?.moveTo(targetIndex)
  state.pendingTeleportIndex.value = null
}

function requireToolboxOpenForStep(state: DriverRuntimeState, requiredToolboxOpen: boolean): void {
  if (!requiredToolboxOpen || state.deps.isToolboxUiBlocked.value) {
    return
  }
  // Ne pas ouvrir la toolbox automatiquement : le tutoriel demande à l’utilisateur de l’ouvrir.
}

function buildDriverConfigForTour(
  state: DriverRuntimeState,
  candidate: OnboardingTourDefinition,
  isStrictFirstRun: boolean,
  stepsCount: number
) {
  return createDriverConfig(
    () => state.route.path,
    candidate.steps,
    (requiredToolboxOpen) => requireToolboxOpenForStep(state, requiredToolboxOpen),
    (message) => state.deps.toastInfo('Etape guidee', message),
    {
      onRequestStepTeleport: (targetIndex) => handleRequestStepTeleport(state, targetIndex, candidate),
      onSyncNextButton: (stepIndex) => syncNextButtonDisabledState(state, stepIndex),
      isStrictFirstRun,
      isToolboxCurrentlyOpen: () => state.deps.toolboxOpen.value,
      stepsCount,
      onStepChange: (stepIndex) => state.deps.onStepChange(candidate, stepIndex),
      onClose: (isCompleted, stepIndex) => state.deps.onClosed(candidate, isCompleted, stepIndex),
    }
  )
}

function computeResumeStepIndex(stored: OnboardingTourProgress | undefined, stepsCount: number): number {
  if (stored?.status !== 'in_progress') {
    return 0
  }
  return Math.min(Math.max(stored.currentStep, 0), stepsCount - 1)
}

function isStrictFirstRunFor(stored: OnboardingTourProgress | undefined): boolean {
  return stored === undefined || stored.status === 'in_progress' || stored.status === 'not_started'
}

async function launchTour(state: DriverRuntimeState, candidate: OnboardingTourDefinition): Promise<boolean> {
  if (import.meta.client) {
    try {
      if (window.localStorage.getItem('monorga.playwright.disableOnboarding') === '1') {
        return false
      }
    } catch {
      // stockage indisponible
    }
  }
  await nextTick()
  // Sécurité : ne jamais laisser deux instances Driver.js actives en parallèle
  // (une instance déjà active pour un autre tour doit être détruite avant de continuer).
  stopCurrentTour(state)
  const resolvedState = state.deps.resolveOnboardingStateMapForChecks()
  state.setStateMap(resolvedState)
  if (shouldSkipTourByStoredState(candidate, resolvedState[candidate.id])) {
    return false
  }
  const resolved = await resolveTourSteps(candidate, state.route.path)
  if (resolved.missingSelectors.length > 0) {
    return false
  }
  // Un autre appel a pu démarrer un driver entre-temps pendant les `await` ci-dessus.
  stopCurrentTour(state)

  const stored = resolvedState[candidate.id]
  const config = buildDriverConfigForTour(
    state,
    candidate,
    isStrictFirstRunFor(stored),
    resolved.steps.length
  )
  const resumeStepIndex = computeResumeStepIndex(stored, resolved.steps.length)
  const instance = state.nuxtApp.$onboardingDriverFactory({
    ...config,
    steps: resolved.steps,
  })
  state.activeDriver.value = instance
  state.activeTourId.value = candidate.id
  state.activeTourSteps.value = candidate.steps
  const nextState = state.deps.updateTourProgress(candidate, 'in_progress', resumeStepIndex)
  state.deps.persistOnboardingStateMap(nextState)
  instance.drive(resumeStepIndex)
  await nextTick()
  syncNextButtonDisabledState(state, resumeStepIndex)
  return true
}

async function startTourGuarded(state: DriverRuntimeState, candidate: OnboardingTourDefinition): Promise<boolean> {
  if (state.isLaunchingTour.value) {
    return false
  }
  state.isLaunchingTour.value = true
  try {
    return await launchTour(state, candidate)
  } finally {
    state.isLaunchingTour.value = false
  }
}

/**
 * Gère le cycle de vie d’une instance Driver.js : démarrage, arrêt, et synchronisation
 * du bouton "Suivant" avec les contraintes de l’étape courante (toolbox ouverte, route requise).
 * Toute la logique métier (progression, verrouillage des clics) reste dans `useOnboarding`
 * et est injectée via `deps`.
 */
export function useOnboardingDriverRuntime(deps: DriverRuntimeDeps) {
  const route = useRoute()
  const nuxtApp = useNuxtApp()
  const onboardingStore = useOnboardingStore()
  const { activeTourId, activeTourSteps, pendingTeleportIndex } = storeToRefs(onboardingStore)
  const { deactivateClickLock } = useOnboardingClickLock()

  const state: DriverRuntimeState = {
    route,
    nuxtApp,
    setStateMap: (nextState) => onboardingStore.setStateMap(nextState),
    activeDriver: ref<Driver | null>(null),
    activeTourId,
    activeTourSteps,
    pendingTeleportIndex,
    toolboxAnimationSyncTimer: ref<number | null>(null),
    // Empêche deux lancements concurrents (deux instances Driver.js superposées)
    // quand plusieurs déclencheurs (watchers) tentent de démarrer un tour en même temps.
    isLaunchingTour: ref(false),
    deactivateClickLock,
    deps,
  }

  return {
    activeDriver: state.activeDriver,
    activeTourId,
    activeTourSteps,
    pendingTeleportIndex,
    toolboxAnimationSyncTimer: state.toolboxAnimationSyncTimer,
    clearToolboxAnimationSyncTimer: () => clearToolboxAnimationSyncTimer(state),
    stopCurrentTour: () => stopCurrentTour(state),
    startTour: (candidate: OnboardingTourDefinition) => startTourGuarded(state, candidate),
    syncNextButtonDisabledState: (stepIndex: number) => syncNextButtonDisabledState(state, stepIndex),
  }
}
